import React, { useEffect, useState } from 'react';
import { Plus, Edit, Trash2, Search, CirclePlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAppSelector } from '@/store/hooks';
import toast from 'react-hot-toast';

interface SliderItem {
    id: number;
    title: string;
    link: string;
    image: string;
    status: 'active' | 'inactive' | '';
}

const OurFaculty: React.FC = () => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL;
    
    const [sliderData, setSliderData] = useState<SliderItem[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const navigate = useNavigate();
    const { token } = useAppSelector((state) => state.auth);

    useEffect(() => {
        fetch(`${baseUrl}/our-faculty`, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        })
            .then(res => res.json())
            .then(data => {
                console.log("Fetched Faculty Data:", data.data); // Debug log
                if (data.success && Array.isArray(data.data)) {
                    console.log("Setting Faculty Data:", data.data); // Debug log
                    setSliderData(data.data);
                }
            })
            .catch(err => {
                console.error("Failed to fetch faculty data:", err);
            });
    }, []);

    const filteredData = sliderData.filter(item =>
        item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.post?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.status?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    console.log("Filtered Faculty Data:", filteredData, sliderData);


    const totalPages = Math.ceil(filteredData.length / entriesPerPage);
    const startIndex = (currentPage - 1) * entriesPerPage;
    const endIndex = startIndex + entriesPerPage;
    const currentData = filteredData.slice(startIndex, endIndex);

    const handleStatusToggle = (id: number) => {
        // Handle status toggle logic
        console.log('Toggle status for id:', id);
    };

    const handleDelete = async (id: string) => {
        try {
            await axios.delete(`${baseUrl}/our-faculty/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            // ✅ Update state immediately
            setSliderData((prev) => prev.filter((faculty) => faculty.id !== id));

            toast.success("Faculty deleted successfully ✅", {
                position: "top-right",
                autoClose: 2000,
            });
        } catch (error: any) {
            console.error("Error deleting faculty:", error.response?.data || error.message);
            // alert(error.response?.data?.message || "Failed to delete faculty");
            toast.error("Failed to delete faculty", {
                position: "top-right",
                autoClose: 2000,
            });
        }
    };


    const handleEdit = (id: number) => {
        // Handle edit logic
        console.log('Edit item with id:', id);
        navigate(`/our-faculty/edit/${id}`);
    };

    const renderStatus = (status: string) => {
        if (status === 'active') {
            return (
                <span className="badge bg-success text-white fs-14 rounded-2">
                    Active
                </span>
            );
        }
        return null; // Return empty for inactive/empty status
    };

    const renderImage = (imageSrc: string, title: string) => {
        return (
            <img src={imageSrc} alt={title} width="100" />
        );
    };

    console.log("Current Faculty Data:", sliderData);

    return (
        <div className="main-container container-fluid">
            {/* PAGE HEADER START */}
            <div className="page-header">
                <div>
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item">
                            <a href="/dashboard">Home</a>
                        </li>
                        <li className="breadcrumb-item active" aria-current="page">
                            Our Faculty
                        </li>
                    </ol>
                </div>
                <button
                    className="btn btn-primary d-flex align-items-center gap-2"
                    onClick={() => navigate('/our-faculty/add')}
                >
                    <Plus size={16} />
                    {/* <CirclePlus /> */}
                    {/* <div className="icon-circle-plus"></div> */}
                    {/* <i className="icon-plus"></i> */}
                    Add Faculty
                </button>
            </div>
            {/* PAGE HEADER END */}

            <div className="row">
                <div className="col-12">
                    <div className="card table-card">
                        <div className="card-body">
                            <div className="table-responsive">
                                <div className="dataTables_wrapper dt-bootstrap5 no-footer">
                                    {/* Controls Row */}
                                    <div className="row">
                                        <div className="col-sm-12 col-md-6">
                                            <div className="dataTables_length">
                                                <label>
                                                    Show{' '}
                                                    <select
                                                        name="entries_length"
                                                        className="form-select form-select-sm"
                                                        value={entriesPerPage}
                                                        onChange={(e) => setEntriesPerPage(Number(e.target.value))}
                                                    >
                                                        <option value={10}>10</option>
                                                        <option value={25}>25</option>
                                                        <option value={50}>50</option>
                                                        <option value={100}>100</option>
                                                    </select>{' '}
                                                    entries
                                                </label>
                                            </div>
                                        </div>
                                        <div className="col-sm-12 col-md-6">
                                            <div className="dataTables_filter">
                                                <label>
                                                    Search:
                                                    <input
                                                        type="search"
                                                        className="form-control form-control-sm"
                                                        placeholder=""
                                                        value={searchTerm}
                                                        onChange={(e) => setSearchTerm(e.target.value)}
                                                    />
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Table */}
                                    <div className="row dt-row">
                                        <div className="col-sm-12">
                                            <table className="table dataTable no-footer">
                                                <thead>
                                                    <tr>
                                                        <th className="sorting sorting_asc" style={{ width: '35px' }}>
                                                            S.No
                                                        </th>
                                                        <th className="sorting" style={{ width: '39px' }}>
                                                            Name
                                                        </th>
                                                        <th className="sorting" style={{ width: '569px' }}>
                                                            Post
                                                        </th>
                                                        <th className="sorting" style={{ width: '569px' }}>
                                                            Experience
                                                        </th>
                                                        <th className="sorting" style={{ width: '82px' }}>
                                                            Image
                                                        </th>
                                                        <th className="sorting" style={{ width: '54px' }}>
                                                            Status
                                                        </th>
                                                        <th className="sorting" style={{ width: '57px' }}>
                                                            Action
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {sliderData.map((item, index) => (
                                                        <tr key={item.id} className={index % 2 === 0 ? 'odd' : 'even'}>
                                                            <td className="sorting_1">{startIndex + index + 1}</td>
                                                            <td>{item.name}</td>
                                                            <td>{item.post}</td>
                                                            <td>{item.experience}</td>
                                                            <td>
                                                                {renderImage(item.photo, item.title)}
                                                            </td>
                                                            <td>
                                                                {renderStatus(item.status)}
                                                            </td>
                                                            <td>
                                                                <div className="d-flex gap-2">

                                                                    <button
                                                                        onClick={() => handleEdit(item._id)}
                                                                        className="btn bg-primary-transparent text-primary p-2 rounded-2 text-center fs-16"
                                                                    >
                                                                        <Edit size={16} />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleDelete(item._id)}
                                                                        className="btn bg-danger-transparent text-danger p-2 rounded-2 text-center fs-16"
                                                                    >
                                                                        <Trash2 size={16} />
                                                                    </button>
                                                                </div>

                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    {/* Pagination */}
                                    <div className="row">
                                        <div className="col-sm-12 col-md-5">
                                            <div className="dataTables_info">
                                                Showing {startIndex + 1} to {Math.min(endIndex, filteredData.length)} of {filteredData.length} entries
                                            </div>
                                        </div>
                                        <div className="col-sm-12 col-md-7">
                                            <div className="dataTables_paginate paging_simple_numbers">
                                                <ul className="pagination">
                                                    <li className={`paginate_button page-item previous ${currentPage === 1 ? 'disabled' : ''}`}>
                                                        <button
                                                            className="page-link"
                                                            onClick={() => setCurrentPage(currentPage - 1)}
                                                            disabled={currentPage === 1}
                                                        >
                                                            Previous
                                                        </button>
                                                    </li>
                                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                                        <li key={page} className={`paginate_button page-item ${page === currentPage ? 'active' : ''}`}>
                                                            <button
                                                                className="page-link"
                                                                onClick={() => setCurrentPage(page)}
                                                            >
                                                                {page}
                                                            </button>
                                                        </li>
                                                    ))}
                                                    <li className={`paginate_button page-item next ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                        <button
                                                            className="page-link"
                                                            onClick={() => setCurrentPage(currentPage + 1)}
                                                            disabled={currentPage === totalPages}
                                                        >
                                                            Next
                                                        </button>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OurFaculty;
